export const HSE_GOAL_STATUSES = [
  "draft",
  "proposed",
  "approved",
  "active",
  "completed",
  "cancelled",
];

export const HSE_GOAL_PURPOSE_KEYS = [
  "health_safety",
  "prevent_harm",
  "systematic",
  "training",
  "environment",
];

export const HSE_GOAL_FOCUS_KEYS = [
  "physical",
  "hse_work",
  "absence",
  "vision",
  "psychosocial",
  "environment",
  "competency",
];

export function currentGoalYear(date = new Date()) {
  const year = date.getFullYear();
  return Number.isInteger(year) ? year : new Date().getFullYear();
}

export function newClientId(prefix = "g") {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function asInt(value, fallback = null) {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  return Number.isInteger(n) ? n : fallback;
}

function asYear(value, fallback = currentGoalYear()) {
  const n = asInt(value, fallback);
  if (!Number.isInteger(n) || n < 2000 || n > 2100) return fallback;
  return n;
}

function datePart(value) {
  if (!value) return null;
  if (typeof value === "string") return value.slice(0, 10);
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  return null;
}

export function yearFromDate(value, fallback = currentGoalYear()) {
  const part = datePart(value);
  if (!part) return fallback;
  return asYear(part.slice(0, 4), fallback);
}

export function yearBounds(year = currentGoalYear()) {
  const y = asYear(year);
  return {
    period_from: `${y}-01-01`,
    period_to: `${y}-12-31`,
    target_on: `${y}-12-31`,
  };
}

export function emptyHseGoal(year = currentGoalYear(), extras = {}) {
  return {
    id: null,
    title: "",
    description: "",
    status: "draft",
    category: extras.category || null,
    indicator: "",
    target_value: "",
    current_value: "",
    ...yearBounds(year),
    responsible_user_id: null,
    approved_by: null,
    approved_at: null,
    signed_by: null,
    signed_at: null,
    hse_action_id: null,
    tiltak: null,
    created_at: null,
    updated_at: null,
    ...extras,
  };
}

export function normalizeHseGoal(raw = {}, fallbackYear = currentGoalYear()) {
  const status = HSE_GOAL_STATUSES.includes(raw.status) ? raw.status : "draft";
  const hseActionId = asInt(raw.hse_action_id ?? raw.tiltak?.id ?? raw.tiltak_id, null);
  const bounds = yearBounds(fallbackYear);

  return {
    id: asInt(raw.id, null),
    title: String(raw.title || "").trim(),
    description: String(raw.description || "").trim(),
    status,
    category: raw.category ? String(raw.category) : null,
    indicator: String(raw.indicator || "").trim(),
    target_value: String(raw.target_value || "").trim(),
    current_value: String(raw.current_value || "").trim(),
    target_on: datePart(raw.target_on) || bounds.target_on,
    period_from: datePart(raw.period_from) || bounds.period_from,
    period_to: datePart(raw.period_to) || bounds.period_to,
    responsible_user_id: asInt(raw.responsible_user_id, null),
    approved_by: raw.approved_by || null,
    approved_at: raw.approved_at || null,
    signed_by: raw.signed_by || null,
    signed_at: raw.signed_at || null,
    hse_action_id: hseActionId && hseActionId > 0 ? hseActionId : null,
    tiltak: raw.tiltak || null,
    created_at: raw.created_at || null,
    updated_at: raw.updated_at || null,
  };
}

export function normalizeHseGoalsList(raw) {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.map((item) => normalizeHseGoal(item));
}

export function goalYear(goal, fallback = currentGoalYear()) {
  return yearFromDate(
    goal?.period_from || goal?.period_to || goal?.target_on || goal?.created_at,
    fallback
  );
}

export function goalsForYear(list, year = currentGoalYear()) {
  const y = asYear(year);
  return normalizeHseGoalsList(list).filter((item) => goalYear(item, y) === y);
}

export function availableGoalYears(list, today = new Date()) {
  const year = currentGoalYear(today);
  const years = new Set(normalizeHseGoalsList(list).map((item) => goalYear(item, year)));
  years.add(year);
  years.add(year - 1);
  return Array.from(years).sort((a, b) => b - a);
}

export function toHseGoalPayload(goal) {
  const next = normalizeHseGoal(goal);
  return {
    title: next.title,
    description: next.description,
    status: next.status,
    category: next.category,
    indicator: next.indicator || null,
    target_value: next.target_value || null,
    current_value: next.current_value || null,
    target_on: next.target_on,
    period_from: next.period_from,
    period_to: next.period_to,
    responsible_user_id: next.responsible_user_id,
    hse_action_id: next.hse_action_id,
  };
}

export function canApproveHseGoal(goal) {
  if (!goal?.id) return false;
  if (goal.approved_at) return false;
  return goal.status === "draft" || goal.status === "proposed";
}

export function canSignHseGoal(goal) {
  return Boolean(goal?.id) && !goal.signed_at;
}

export function upsertGoalInList(list, goal) {
  const next = normalizeHseGoal(goal);
  const others = normalizeHseGoalsList(list).filter((item) => item.id !== next.id);
  return [next, ...others];
}

export function toggleListValue(list, value) {
  const current = Array.isArray(list) ? list : [];
  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
}
